import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { v4 as uuidv4 } from "uuid";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { useCreateErrorMutation } from "@/services/backend/sample-error/service";
import { Input } from "@/shared/components/ui/input";
import { EvaluableType } from "@/services/backend/sample-error/sample-error";
import EvaluableTypeSelector from "@/shared/components/domain/samples/evaluable-type-selector";
import { useEffect, useState } from "react";

const FormSchema = z.object({
  description: z.string(),
  evaluableType: z.string(),
});

export function PutErrorDialogForm() {
  const [isOpen, setIsOpen] = useState(false);
  const [createError, { isLoading, isSuccess }] = useCreateErrorMutation();
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
  });

  useEffect(() => {
    function reset(): void {
      if (isSuccess) {
        form.reset();
        setIsOpen(false);
      }
    }

    reset();
  }, [form, isSuccess]);

  function onSubmit(data: z.infer<typeof FormSchema>) {
    const id = uuidv4();
    createError({
      sampleError: {
        id,
        originalId: id,
        description: data.description,
        evaluableType: data.evaluableType as EvaluableType,
      },
    });
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="default">
          <p>Fehler anlegen</p>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Fehler dokumentieren</DialogTitle>
          <DialogDescription>
            Bitte definieren Sie, welche Fehler bei den Proben vorliegen
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center space-x-2">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="w-full space-y-6"
            >
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Beschreibung</FormLabel>
                    <FormControl>
                      <Input
                        disabled={isLoading}
                        placeholder="Beschreibung"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>Beschreibung des Fehlers</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="evaluableType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Auswertbarkeit</FormLabel>
                    <FormControl>
                      <EvaluableTypeSelector
                        disabled={isLoading}
                        evaluableType={field.value}
                        setValue={field.onChange}
                      />
                    </FormControl>
                    <FormDescription>Auswertbarkeit</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button disabled={isLoading} type="submit">
                Submit
              </Button>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
